import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { 
  FileSpreadsheet, 
  Sparkles, 
  Mail, 
  Lock, 
  User, 
  ArrowRight,
  Loader2,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

// Detect if running in an in-app browser (WebView)
const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  // Common in-app browser patterns
  const inAppPatterns = [
    'FBAN', 'FBAV',           // Facebook
    'Instagram',              // Instagram  
    'LinkedIn',               // LinkedIn
    'Twitter',                // Twitter
    'Line/',                  // Line
    'KAKAOTALK',              // KakaoTalk
    'Snapchat',               // Snapchat
    'WhatsApp',               // WhatsApp
    'Telegram',               // Telegram
    'WeChat', 'MicroMessenger', // WeChat
    'WebView',                // Generic WebView
    'wv'                      // Android WebView
  ];
  
  return inAppPatterns.some(pattern => ua.includes(pattern));
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, loginWithGoogle, error: authError } = useAuth();
  
  // Determine mode from URL path
  const isSignupPath = location.pathname === '/signup';
  const [isLogin, setIsLogin] = useState(!isSignupPath);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isWebView, setIsWebView] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  // Capture UTM parameters
  const [utmParams, setUtmParams] = useState({});
  const [referralCode, setReferralCode] = useState('');

  // Update mode when URL changes
  useEffect(() => {
    setIsLogin(location.pathname !== '/signup');
  }, [location.pathname]);

  useEffect(() => {
    // Check for WebView
    setIsWebView(isInAppBrowser());
    
    // Get UTM params from URL
    const utm = {
      utm_source: searchParams.get('utm_source'),
      utm_medium: searchParams.get('utm_medium'),
      utm_content: searchParams.get('utm_content'),
      utm_campaign: searchParams.get('utm_campaign')
    };
    setUtmParams(utm);
    
    // Get referral code
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        // Include UTM params and referral code in registration
        await register(formData.name, formData.email, formData.password, {
          ...utmParams,
          referral_code: referralCode
        });
      }
      
      // Check for pending subscription action
      const pendingSubscription = localStorage.getItem('pendingSubscription');
      if (pendingSubscription) {
        const { returnUrl } = JSON.parse(pendingSubscription);
        // Keep the pendingSubscription in localStorage - PricingPage will handle it
        navigate(returnUrl || '/pricing/subscription');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-800 p-12 flex-col justify-between relative overflow-hidden">
        {/* Abstract shapes */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-48 h-48 bg-rose-500/20 rounded-full blur-2xl"></div>
        
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-indigo-600" />
            </div>
            <span className="text-2xl font-heading font-bold text-white">CRM Data Cleaner</span>
          </Link>
          
          <h1 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-6 leading-tight">
            Clean your data<br />
            <span className="text-indigo-200">before it cleans you.</span>
          </h1>
          
          <p className="text-lg text-indigo-100 max-w-md">
            Transform messy spreadsheets into CRM-ready data in minutes. 
            No formulas. No code. Just clean data.
          </p>
        </div>
        
        <div className="relative z-10 space-y-4">
          <div className="flex items-center gap-3 text-white/80">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <span>AI-powered cleaning suggestions</span>
          </div>
          <div className="flex items-center gap-3 text-white/80">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-4 h-4" />
            </div>
            <span>Support for Excel & CSV files</span>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-heading font-bold text-slate-900">CRM Data Cleaner</span>
          </Link>
          
          <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                {isLogin ? 'Welcome back' : 'Create account'}
              </h2>
              <p className="text-slate-600">
                {isLogin 
                  ? 'Sign in to continue cleaning your data' 
                  : 'Start cleaning your data in minutes'}
              </p>
            </div>
            
            {/* WebView Warning */}
            {isWebView && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Google login unavailable in this browser
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      For security reasons, Google doesn&apos;t allow sign-in from in-app browsers. 
                      Please open in your device&apos;s browser or use email login below.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 text-amber-700 border-amber-300 hover:bg-amber-100"
                      onClick={() => {
                        // Try to open in external browser
                        const url = window.location.href;
                        // For iOS
                        window.location.href = `googlechrome://${url.replace(/^https?:\/\//, '')}`;
                        // Fallback - copy URL
                        setTimeout(() => {
                          navigator.clipboard?.writeText(url);
                          alert('Link copied! Please paste in Safari or Chrome.');
                        }, 500);
                      }}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open in Browser
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Google Login - hide completely in WebView or show disabled */}
            {!isWebView ? (
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 text-base font-medium mb-6"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            ) : (
              <div className="mb-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 text-base font-medium opacity-50 cursor-not-allowed"
                  disabled
                >
                  <svg className="w-5 h-5 mr-3 grayscale" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google (unavailable in this browser)
                </Button>
              </div>
            )}
            
            <div className="relative mb-6">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-sm text-slate-400">
                {isWebView ? 'use email instead' : 'or'}
              </span>
            </div>
            
            {/* Error message */}
            {(error || authError) && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error || authError}</span>
              </div>
            )}
            
            {/* Email Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      className="pl-10 h-11"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10 h-11"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {isLogin && (
                    <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-11"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {isLogin ? 'Sign in' : 'Create account'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-center text-sm text-slate-600 mt-6">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <Link
                to={isLogin ? '/signup' : '/login'}
                className="ml-1 text-indigo-600 hover:text-indigo-700 font-medium"
                onClick={() => setError('')}
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
